/**
 * The single definition of the app day for the web client (FLIP-218).
 *
 * An app day runs from 3:59am local to 3:59am local the following morning, rather than
 * midnight to midnight. For a chronic-pain audience, use at 4am is a core scenario rather than
 * an edge case: a flare-up at 4am should produce a score, a Sanctuary session and a plan check
 * that all land on the same day.
 *
 * The boundary is defined once per stack. The C# counterpart is AppDay.BoundaryMinutes in
 * BrainChanger.Domain/Models/Common/AppDay.cs, and the SQL counterpart is the
 * DATEADD(minute, -239, ...) expression in the Nova views and the MarginOfSafetyLastScore
 * stored procedure. All three must move together.
 *
 * Was 299 (4:59am) before FLIP-218.
 */
export const APP_DAY_BOUNDARY_MINUTES = 239;

/**
 * The hour a newly created plan is stamped at.
 *
 * Chosen to sit just inside the app day rather than just before it: at UTC midnight a plan fell
 * on the wrong side of its own 3:59am boundary, which is what forced the midnight-to-midnight
 * range workaround the plan widgets used to carry. 04:00 also makes the stored value
 * self-describing — a StartDate whose time is 04:00 is a day marker, not an event timestamp.
 */
export const APP_DAY_PLAN_START_HOUR = 4;

export class DateHelper {
  /**
   * Gets a local date adjusted for timezone to prevent UTC conversion issues when sending to API
   * @param date Optional date to use, defaults to current date
   * @returns Date object that represents local midnight adjusted for timezone offset
   */
  public static getLocalDate(date?: Date): Date {
    const targetDate = date || new Date();

    // Set to local midnight
    const localDate = new Date(targetDate);
    // localDate.setHours(0, 0, 0, 0);

    // Get timezone offset in milliseconds and adjust to prevent UTC conversion issues
    const offsetMs = localDate.getTimezoneOffset() * 60000;
    const adjustedDate = new Date(localDate.getTime() - offsetMs);

    return adjustedDate;
  }

  /**
   * Gets the current local date as a timezone-safe date for API consumption
   * @returns Date object representing today in user's timezone
   */
  public static getTodayLocal(): Date {
    return this.getLocalDate();
  }

  /**
   * Formats the current local date to YYYY-MM-DD HH:mm:ss format for API consumption.
   *
   * Reads the wall clock directly rather than going through getLocalDate(). getLocalDate()
   * shifts the instant by the timezone offset so that *UTC* serialisation of the Date reads as
   * local — correct when the value is sent as a Date and serialised to ISO, but reading local
   * getters off that shifted value applies the offset a second time. That made this return
   * local + offset: an hour ahead in UTC+1, ten hours ahead in UTC+10 (FLIP-218). The string
   * form wants the plain wall clock, matching score.service.ts's moment().format(...).
   *
   * @param date Optional date to format, defaults to now
   * @returns Formatted local date string in YYYY-MM-DD HH:mm:ss format
   */
  public static formatToApiDateTime(date?: Date): string {
    const localDate = date || new Date();
    const year = localDate.getFullYear();
    const month = String(localDate.getMonth() + 1).padStart(2, "0");
    const day = String(localDate.getDate()).padStart(2, "0");
    const hours = String(localDate.getHours()).padStart(2, "0");
    const minutes = String(localDate.getMinutes()).padStart(2, "0");
    const seconds = String(localDate.getSeconds()).padStart(2, "0");

    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  }

  /**
   * The most recent app-day boundary (3:59am local) — content dated before it belongs to the
   * previous day. Matches ScoreLocalService.getCurrentDayBoundary()'s reset timing.
   * @param now Optional reference time, defaults to now
   * @returns Date object for the most recent boundary
   */
  public static getCurrentDayBoundary(now: Date = new Date()): Date {
    const boundary = this.boundaryOnSameDateAs(now);
    if (now < boundary) {
      boundary.setDate(boundary.getDate() - 1);
    }
    return boundary;
  }

  /**
   * The next upcoming app-day boundary, for scheduling a precise one-shot timer rather than
   * polling.
   * @param now Optional reference time, defaults to now
   * @returns Date object for the next boundary
   */
  public static getNextDayBoundary(now: Date = new Date()): Date {
    const boundary = this.boundaryOnSameDateAs(now);
    if (now >= boundary) {
      boundary.setDate(boundary.getDate() + 1);
    }
    return boundary;
  }

  /**
   * Midnight of the "app day" — the calendar date the boundary belongs to.
   *
   * Use this to answer "which day is it, for this user, right now": between midnight and the
   * 3:59am boundary it returns yesterday's date, which is what makes a 4am score and a 4am plan
   * agree. PlanService.getTodayPlanId() resolves "today" through here before matching.
   *
   * It returns a DAY, not an instant, and that is the point — plan start dates are matched with
   * isPlanOnDay() on date components rather than by comparing instants against a boundary.
   * @param now Optional reference time, defaults to now
   * @returns Date object at midnight of the app day
   */
  public static getCurrentAppDay(now: Date = new Date()): Date {
    const boundary = this.getCurrentDayBoundary(now);
    return new Date(boundary.getFullYear(), boundary.getMonth(), boundary.getDate());
  }

  /**
   * The boundary instant on the same calendar date as the supplied time, before any
   * adjustment for whether that instant has already passed. Built from
   * APP_DAY_BOUNDARY_MINUTES so the reset time is stated once.
   */
  private static boundaryOnSameDateAs(now: Date): Date {
    return new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
      0,
      APP_DAY_BOUNDARY_MINUTES,
      0,
      0
    );
  }

  /**
   * The timestamp a plan for the given calendar day should be saved with: local 04:00 on that
   * day, sent as UTC 04:00 so the date survives the round trip without a timezone shift.
   *
   * Previously plans were stamped at UTC midnight, which put them *before* the 4:59am boundary
   * of their own app day, so both plan widgets had to match a midnight-to-midnight range
   * instead. Reading is now done by DateHelper.isPlanOnDay, which compares the same UTC date
   * components this writes — see there for why that is preferred over comparing the parsed
   * instant against the boundary (FLIP-218).
   *
   * @param day The calendar day the plan is for
   * @returns Date stamped at 04:00 for that day
   */
  public static buildPlanStartDate(day: Date): Date {
    const startDate = new Date();
    startDate.setUTCFullYear(day.getFullYear(), day.getMonth(), day.getDate());
    startDate.setUTCHours(APP_DAY_PLAN_START_HOUR, 0, 0, 0);
    return startDate;
  }

  /**
   * Whether a plan's stored startDate is the plan for `day`.
   *
   * The read side of buildPlanStartDate, and it reads the SAME components that one writes: the
   * UTC year/month/date. A plan's startDate is a day marker, not an instant, and comparing it
   * as an instant against a local boundary only works while the client's offset is positive —
   * at UTC-5 a plan stamped 04:00Z parses to 23:00 the previous evening and falls outside its
   * own app day. Comparing the date components is offset-independent, so it holds for every
   * user rather than only the Australian ones (FLIP-218).
   *
   * @param startDate The plan's stored start date
   * @param day The day to test against, as a local calendar date
   */
  public static isPlanOnDay(startDate: string | Date, day: Date): boolean {
    const planDate = new Date(startDate);
    return (
      planDate.getUTCFullYear() === day.getFullYear() &&
      planDate.getUTCMonth() === day.getMonth() &&
      planDate.getUTCDate() === day.getDate()
    );
  }

  /**
   * The date-key component of a Plan draft's localStorage key (FLIP-301), keyed by the plan's
   * own start date rather than the date the draft was written.
   *
   * Reads the same UTC year/month/date components isPlanOnDay compares, so a draft written from
   * a startDate built by buildPlanStartDate always keys to the same string a later isPlanOnDay
   * check would match against. Callers holding a local calendar day rather than an already-built
   * startDate (e.g. PlanLifecycleService.selectedDate before a plan exists) should pass
   * `buildPlanStartDate(day)` here rather than `day` itself, so the UTC components read back are
   * the ones that day's plan would actually be stamped with — reading local components off a
   * plain local Date would produce a different string for the same logical day at UTC offsets
   * where the two disagree.
   *
   * @param startDate A plan start date (or `buildPlanStartDate(day)` for a day with no plan yet)
   * @returns A "YYYY-MM-DD" string built from UTC date components
   */
  public static buildDraftDateKey(startDate: Date): string {
    const year = startDate.getUTCFullYear();
    const month = String(startDate.getUTCMonth() + 1).padStart(2, "0");
    const day = String(startDate.getUTCDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  /**
   * Gets a UTC DateTime from a local date
   * @param date The date to convert
   * @returns Date object in UTC
   */
  public static getUTCDateTime(date: Date): Date {
    const result = new Date();
    result.setUTCFullYear(date.getFullYear(), date.getMonth(), date.getDate());
    result.setUTCHours(
      date.getHours(),
      date.getMinutes(),
      date.getSeconds(),
      date.getMilliseconds()
    );
    return result;
  }
}
